import Tooltip from "../../ui/ToolTip"
import { FaInfoCircle } from "react-icons/fa";
import { useEffect, useMemo, useState } from "react";

const InputFormValue = ({ 
  className = "text-xs rounded-l-none rounded-r-lg bg-gray-50 border text-gray-900 focus:ring-blue-500 focus:border-blue-500 block flex-1 min-w-0 w-full text-sm border-gray-300 p-2.5",
  required, 
  title, 
  value, 
  setValue, 
  icon, 
  placeholder, 
  type, 
  options, 
  min, 
  max, 
  info, 
  disabled,
  minChars = 2, 
  rows = 1,
  onSelectOption, 
  maxHeight = "max-h-56",
  suffix,
  onClick = null,
}) => {
  // Local state for autocomplete type
  const [query, setQuery] = useState("")
  const [openAutocomplete, setOpenAutocomplete] = useState(false)

  useEffect(() => {
    if (type !== "autocomplete") return
    const selectedOption = (options || []).find(o => o.value === value)
    if (selectedOption) {
      setQuery(selectedOption.label)
    } else if (!value) {
      setQuery("")
    }
  }, [type, value, options])

  const filteredOptions = useMemo(() => {
    if (type !== "autocomplete") return []
    const lower = (query || "").toLowerCase()
    return (options || [])
      .filter(o => o.label?.toLowerCase().includes(lower))
      // .slice(0, 10)
  }, [type, options, query])

  return (
    <div>
      <label className="flex flex-row items-center text-xs block text-gray-700 mb-1">
        {title}
        {required && <span className="text-red-500">*</span>}
        {info && 
          <Tooltip content={info}>
            <div className="inline-block ml-1 hover:cursor-pointer">
              <FaInfoCircle className="inline-block ml-1" />
            </div>
          </Tooltip>
        }
      </label>
      <div className={`flex ${type === "multiSelect" ? "items-start" : ""}`}>
        <span
          className={`text-xs inline-flex items-center px-3 text-gray-900 bg-gray-200 border rounded-e-0 border-gray-300 border-e-0 rounded-s-md ${
            type === "multiSelect" ? "self-stretch min-h-[2.5rem] items-center" : ""
          }`}
        >
          {icon}
        </span>
        {type === "autocomplete" ? (
          <div 
            className={`relative w-full ${onClick && disabled ? 'cursor-pointer' : ''}`}
            onClick={onClick && disabled ? onClick : undefined}
          >
            <input
              type="text"
              placeholder={placeholder}
              value={query}
              required={required}
              disabled={disabled && !onClick}
              readOnly={disabled && onClick ? true : false}
              onChange={(e) => { setQuery(e.target.value); setOpenAutocomplete(e.target.value.length >= minChars) }}
              onFocus={() => setOpenAutocomplete((query || "").length >= minChars)}
              onBlur={() => setTimeout(() => setOpenAutocomplete(false), 150)}
              onClick={onClick && !disabled ? onClick : undefined}
              className={`${className} ${suffix ? 'pr-16' : 'pr-8'} ${onClick && !disabled ? 'cursor-pointer' : ''} ${onClick && disabled ? 'cursor-pointer' : ''}`}
            />
            {(query || value) && !disabled && (
              <button
                type="button"
                aria-label="Limpiar selección"
                className={`absolute ${suffix ? 'right-10' : 'right-2'} top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600`}
                onMouseDown={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setQuery("")
                  setValue("")
                  if (onSelectOption) onSelectOption(null)
                  setOpenAutocomplete(false)
                }}
                title="Limpiar"
              >
                ×
              </button>
            )}
            {suffix && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 h-full flex items-center">
                {suffix}
              </div>
            )}
            {openAutocomplete && (query || "").length >= minChars && filteredOptions.length > 0 && (
              <ul className={`absolute z-50 bg-white shadow-lg border rounded-md mt-1 ${maxHeight} overflow-auto w-full text-sm shadow`}>
                {filteredOptions.map((opt) => (
                  <li
                    key={opt.value}
                    className="px-3 text-xs text-gray-900 py-2 hover:bg-gray-100 cursor-pointer"
                    onMouseDown={() => {
                      setValue(opt.value)
                      setQuery(opt.label)
                      if (onSelectOption) onSelectOption(opt)
                      setOpenAutocomplete(false)
                    }}
                    title={opt.label}
                  >
                    {opt.label}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : type === "select" ? (
          <div className="relative w-full">
            <select
              type={type}
              placeholder={placeholder}
              value={value}
              required={required}
              disabled={disabled}
              onChange={(e) => { 
                const val = e.target.value;
                setValue(val); 
                if (onSelectOption) {
                  const selectedOption = (options || []).find(o => String(o.value) === String(val)) || { value: val, label: '' };
                  onSelectOption(selectedOption);
                }
              }}
              className={`${className} ${suffix ? 'pr-16' : ''}`}
            >
              { (options || []).map((option, i) => (
                <option key={i} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {suffix && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 h-full flex items-center">
                {suffix}
              </div>
            )}
          </div>
        ) : type === "multiSelect" ? (
          <div className={`relative w-full flex flex-col gap-2 ${className}`}>
            {placeholder && (!Array.isArray(value) || value.length === 0) && (
              <span className="text-[11px] text-gray-400">{placeholder}</span>
            )}
            {Array.isArray(value) && value.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {value.map((id) => {
                  const opt = (options || []).find((o) => String(o.value) === String(id))
                  const label = opt?.label || String(id)
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 pl-1.5 pr-1 py-0.5 rounded-md bg-orange-100 text-orange-900 text-[11px] max-w-full"
                    >
                      <span className="truncate">{label}</span>
                      <button
                        type="button"
                        disabled={disabled}
                        className="flex-shrink-0 rounded px-0.5 text-orange-700 hover:bg-orange-200/80 disabled:opacity-40"
                        aria-label={`Quitar ${label}`}
                        onClick={() => {
                          const next = value.filter((x) => String(x) !== String(id))
                          setValue(next)
                        }}
                      >
                        ×
                      </button>
                    </span>
                  )
                })}
              </div>
            )}
            <div
              className={`${
                Array.isArray(value) && value.length > 0 ? "border-t border-gray-200 pt-2 " : ""
              } -mx-0.5 ${maxHeight} overflow-y-auto flex flex-col gap-1`}
            >
              {(options || []).length === 0 ? (
                <span className="text-[11px] text-gray-400 px-2 py-1">Sin opciones</span>
              ) : (
                (options || []).map((option) => {
                  const id = String(option.value)
                  const selected = Array.isArray(value) && value.some((x) => String(x) === id)
                  return (
                    <label
                      key={id}
                      className={`flex items-center gap-2 cursor-pointer text-xs px-2 py-1 rounded hover:bg-gray-100 ${
                        disabled ? "opacity-50 pointer-events-none" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-orange-500 focus:ring-orange-400"
                        checked={selected}
                        disabled={disabled}
                        onChange={() => {
                          const arr = Array.isArray(value) ? [...value] : []
                          const next = selected
                            ? arr.filter((x) => String(x) !== id)
                            : [...arr, option.value]
                          setValue(next)
                        }}
                      />
                      <span className="truncate">{option.label}</span>
                    </label>
                  )
                })
              )}
            </div>
            {suffix && (
              <div className="absolute right-2 top-2 h-full flex items-start">
                {suffix}
              </div>
            )}
          </div>
        ) : type === "textarea" ? (
          <div 
            className={`relative w-full ${onClick && disabled ? 'cursor-pointer' : ''}`}
            onClick={onClick && disabled ? onClick : undefined}
          >
            <textarea
              placeholder={placeholder}
              value={value}
              rows={rows}
              required={required}
              disabled={disabled && !onClick}
              readOnly={disabled && onClick ? true : false}
              onChange={(e) => setValue(e.target.value)}
              onClick={onClick && !disabled ? onClick : undefined}
              className={`${className} ${suffix ? 'pr-16' : ''} ${onClick && !disabled ? 'cursor-pointer' : ''} ${onClick && disabled ? 'cursor-pointer' : ''}`}
            />
            {suffix && (
              <div className="absolute right-2 top-2 h-full flex items-start">
                {suffix}
              </div>
            )}
          </div>
        ) : type === "date" ? (
          <div 
            className={`relative w-full ${onClick && disabled ? 'cursor-pointer' : ''}`}
            onClick={onClick && disabled ? onClick : undefined}
          >
            <input
              type="date"
              placeholder={placeholder}
              value={value}
              min={min}
              max={max}
              required={required}
              disabled={disabled && !onClick}
              readOnly={disabled && onClick ? true : false}
              onChange={(e) => setValue(e.target.value)}
              onClick={onClick && !disabled ? onClick : undefined}
              className={`${className} ${suffix ? 'pr-16' : ''} ${onClick && !disabled ? 'cursor-pointer' : ''} ${onClick && disabled ? 'cursor-pointer' : ''}`} 
            />
            {suffix && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 h-full flex items-center">
                {suffix}
              </div>
            )}
          </div>
        ) : type === "number" ? (
          <div 
            className={`relative w-full ${onClick && disabled ? 'cursor-pointer' : ''}`}
            onClick={onClick && disabled ? onClick : undefined}
          >
            <input
              type="number"
              placeholder={placeholder}
              value={value}
              min={min}
              max={max}
              required={required}
              disabled={disabled && !onClick}
              readOnly={disabled && onClick ? true : false}
              onChange={(e) => setValue(e.target.value)}
              onClick={onClick && !disabled ? onClick : undefined}
              className={`${className} ${suffix ? 'pr-16' : ''} ${onClick && !disabled ? 'cursor-pointer' : ''} ${onClick && disabled ? 'cursor-pointer' : ''}`} 
            />
            {suffix && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 h-full flex items-center">
                {suffix}
              </div>
            )}
          </div>
        ) : (
          <div 
            className={`relative w-full ${onClick && disabled ? 'cursor-pointer' : ''}`}
            onClick={onClick && disabled ? onClick : undefined}
          >
            <input
              type={type}
              placeholder={placeholder}
              value={value}
              min={min}
              max={max}
              required={required}
              disabled={disabled && !onClick}
              readOnly={disabled && onClick ? true : false}
              onChange={(e) => setValue(e.target.value)}
              onClick={onClick && !disabled ? onClick : undefined}
              className={`${className} ${suffix ? 'pr-16' : ''} ${onClick && !disabled ? 'cursor-pointer' : ''} ${onClick && disabled ? 'cursor-pointer' : ''}`} 
            />
            {suffix && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 h-full flex items-center">
                {suffix}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default InputFormValue;
